/**
 * chain-eos/balance.ts
 *
 * Token balance loading for EOS accounts.
 * Vaulta (A) is displayed first, then EOS, then other tokens.
 *
 * Fetches balances in parallel for fast loading.
 */

import { AccountKit } from '@wharfkit/account'
import { APIClient, Asset } from '@wharfkit/antelope'
import { Chains } from '@wharfkit/common'
import { HyperionAPIClient, Types } from '@wharfkit/hyperion'
import {
  EOS_RPC_ENDPOINTS,
  HYPERION_ENDPOINTS,
  TOKENS,
  TOKEN_DISPLAY_ORDER,
  resolveTokenLogoUrl,
  isKnownTokenContract,
  type TokenSymbol,
} from './config.js'

/**
 * Hyperion v2 token row (automatic discovery via get_tokens).
 *
 * NOTE: `@wharfkit/hyperion`'s `Types.v2.TokenInfo` shape has differed between
 * versions (e.g. `currency` vs `symbol`, `decimals` vs `precision`). We keep a
 * compatible subset here to avoid build-time type breakage in CI.
 */
export type HyperionTokenInfo =
  | Types.v2.TokenInfo
  | {
      contract?: string
      currency?: string
      symbol?: string
      amount?: string | number
      decimals?: number
      precision?: number
    }

export interface TokenBalance {
  symbol: TokenSymbol | string
  contract: string
  amount: string       // e.g. "123.4567"
  amountRaw: number    // numeric for calculations
  name: string
  precision: number
  /** Public image URL from token config (TOKENS.*.logoUrl) */
  logoUrl?: string
  usdValue?: number    // optional, populated by price feed
  /** True for priority tokens (A/EOS/USDT) and known/verified contracts (see KNOWN_TOKEN_CONTRACTS). */
  isKnown?: boolean
}

export interface AccountResources {
  cpu: {
    used: number
    available: number
    max: number
    percentUsed: number
  }
  net: {
    used: number
    available: number
    max: number
    percentUsed: number
  }
  ram: {
    used: number
    quota: number
    percentUsed: number
  }
}

// ─── RPC client with failover ──────────────────────────────────────────────────

function getClient(endpointIndex = 0): APIClient {
  const url = EOS_RPC_ENDPOINTS[endpointIndex % EOS_RPC_ENDPOINTS.length]
  return new APIClient({ url })
}

function getAccountKit(): AccountKit {
  return new AccountKit(Chains.EOS, {
    client: new APIClient({ url: EOS_RPC_ENDPOINTS[0] }),
  })
}

// ─── Balance fetching ─────────────────────────────────────────────────────────

async function fetchTokenBalance(
  accountName: string,
  contract: string,
  symbol: string,
  endpointIndex = 0,
): Promise<string> {
  try {
    const client = getClient(endpointIndex)
    const result = await client.v1.chain.get_currency_balance(contract, accountName, symbol)
    if (result && result.length > 0) {
      const asset = Asset.from(result[0].toString())
      return asset.value.toFixed(asset.symbol.precision)
    }
    return (0).toFixed(4)
  } catch (err) {
    // Retry on next endpoint
    if (endpointIndex < EOS_RPC_ENDPOINTS.length - 1) {
      return fetchTokenBalance(accountName, contract, symbol, endpointIndex + 1)
    }
    console.warn(`[balance] Failed to fetch ${symbol} for ${accountName}:`, err)
    return (0).toFixed(4)
  }
}

/**
 * Load balances for all known tokens in priority order (A first, then EOS, etc.)
 */
export async function loadTokenBalances(accountName: string): Promise<TokenBalance[]> {
  const fetches = TOKEN_DISPLAY_ORDER.map(async (sym): Promise<TokenBalance> => {
    const token = TOKENS[sym]
    const rawAmount = await fetchTokenBalance(accountName, token.contract, token.symbol)
    const amountNum = parseFloat(rawAmount)
    return {
      symbol: sym,
      contract: token.contract,
      amount: rawAmount,
      amountRaw: amountNum,
      name: token.name,
      precision: token.precision,
      logoUrl: token.logoUrl,
      isKnown: true,
    }
  })

  return Promise.all(fetches)
}

/**
 * Load just the Vaulta (A) balance — used for quick display on lock screen.
 */
export async function loadVaultaBalance(accountName: string): Promise<TokenBalance> {
  const token = TOKENS['A']
  const rawAmount = await fetchTokenBalance(accountName, token.contract, token.symbol)
  const amountNum = parseFloat(rawAmount)
  return {
    symbol: 'A',
    contract: token.contract,
    amount: rawAmount,
    amountRaw: amountNum,
    name: token.name,
    precision: token.precision,
    logoUrl: token.logoUrl,
    isKnown: true,
  }
}

// ─── Resources ────────────────────────────────────────────────────────────────

/** Detects HTTP 400 / account_query_exception from chain API clients (WharfKit / fetch). */
function isLikelyGetAccount400Error(e: unknown): boolean {
  if (e == null || typeof e !== 'object') return false
  const err = e as Record<string, unknown>
  if (err.status === 400) return true
  const msg = String(err.message ?? '')
  if (/\b400\b/.test(msg) || msg.includes('account_query_exception')) return true
  const nested = err.error
  if (nested && typeof nested === 'object') {
    const n = nested as Record<string, unknown>
    if (n.code === 400) return true
    if (String(n.what ?? '').includes('account_query_exception')) return true
  }
  return false
}

export async function loadAccountResources(accountName: string): Promise<AccountResources> {
  if (accountName == null || accountName === undefined) {
    throw new Error('loadAccountResources: account name is required')
  }
  const trimmed = String(accountName).trim()
  if (trimmed.length === 0) {
    throw new Error('loadAccountResources: account name cannot be empty')
  }

  try {
    console.log('[balance] loadAccountResources called with:', trimmed)
    const accountKit = getAccountKit()
    const account = await accountKit.load(trimmed)
    const d = account.data

    const cpuUsed = Number(d.cpu_limit.used)
    const cpuMax = Number(d.cpu_limit.max)
    const cpuAvail = Number(d.cpu_limit.available)

    const netUsed = Number(d.net_limit.used)
    const netMax = Number(d.net_limit.max)
    const netAvail = Number(d.net_limit.available)

    const ramUsed = Number(d.ram_usage)
    const ramQuota = Number(d.ram_quota)

    // total_resources available on d for stake UI when needed (self_delegated_bandwidth, etc.)

    return {
      cpu: {
        used: cpuUsed,
        available: cpuAvail,
        max: cpuMax,
        percentUsed: cpuMax > 0 ? Math.round((cpuUsed / cpuMax) * 100) : 0,
      },
      net: {
        used: netUsed,
        available: netAvail,
        max: netMax,
        percentUsed: netMax > 0 ? Math.round((netUsed / netMax) * 100) : 0,
      },
      ram: {
        used: ramUsed,
        quota: ramQuota,
        percentUsed: ramQuota > 0 ? Math.round((ramUsed / ramQuota) * 100) : 0,
      },
    }
  } catch (e) {
    if (isLikelyGetAccount400Error(e)) {
      console.error('[balance] loadAccountResources: AccountKit.load failed (400)', {
        accountName: trimmed,
        error: e,
      })
    }
    throw e
  }
}

/**
 * Discover ALL token balances for an account via Hyperion v2 `get_tokens`,
 * then merge with the known priority tokens (A first, EOS, USDT).
 *
 * Priority tokens always appear first (even with zero balance).
 * Additional tokens found on chain are appended, sorted by balance desc.
 * Returns a full TokenBalance[] ready for the UI — including logoUrls for
 * known tokens and `usdValue` placeholder (caller must call fetchTokenPrices).
 */
export async function loadAllTokenBalances(accountName: string): Promise<TokenBalance[]> {
  const trimmed = String(accountName ?? '').trim()
  if (trimmed.length === 0) return []

  // 1. Always load priority tokens via direct RPC (fast + reliable)
  const priorityBalances = await loadTokenBalances(trimmed)

  // 2. Discover extra tokens from Hyperion.
  // Prefer raw fetch: WharfKit decoder throws if any token row has a weird symbol shape
  // (DecodingError), which previously aborted the whole discovery and hid extras.
  //
  // IMPORTANT: query every configured Hyperion endpoint and MERGE the results,
  // rather than stopping at the first one that merely responds. Different
  // Hyperion nodes can have gaps in their own indexing for a given
  // contract/account — e.g. eos.hyperion.eosrio.io was observed missing
  // btc.xsat entirely for an account that eos.eosusa.io correctly reported a
  // nonzero balance for. "First endpoint that answers 200 OK wins" silently
  // hid that token because the first node's incomplete-but-valid response was
  // accepted as good and the others were never tried. See docs/LEARNINGS.md.
  const fetchTokensFrom = async (endpoint: string): Promise<HyperionTokenInfo[] | null> => {
    try {
      const url = `${endpoint}/v2/state/get_tokens?account=${encodeURIComponent(trimmed)}`
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const body = (await res.json()) as { tokens?: HyperionTokenInfo[] }
      return Array.isArray(body.tokens) ? [...body.tokens] : []
    } catch (e) {
      console.warn(`[balance] Hyperion get_tokens failed on ${endpoint}:`, e)
      // Fallback: typed client (may still DecodingError on bad rows)
      try {
        const hyperion = new HyperionAPIClient(new APIClient({ url: endpoint }))
        const result = await hyperion.v2.state.get_tokens(trimmed)
        return [...(result.tokens ?? [])]
      } catch (e2) {
        console.warn(`[balance] Hyperion typed get_tokens also failed on ${endpoint}:`, e2)
        return null
      }
    }
  }

  const perEndpointResults = await Promise.all(HYPERION_ENDPOINTS.map(fetchTokensFrom))

  // Union by contract::symbol. When multiple nodes disagree on a token's
  // amount (indexing lag), keep the larger nonzero value rather than
  // whichever endpoint happened to be merged first.
  const mergedByKey = new Map<string, HyperionTokenInfo>()
  let hyperionOk = false
  for (const tokens of perEndpointResults) {
    if (tokens == null) continue
    hyperionOk = true
    for (const t of tokens) {
      const sym = typeof (t as any).currency === 'string' ? (t as any).currency : (t as any).symbol
      if (!sym) continue
      const key = `${String((t as any).contract)}::${sym}`
      const existing = mergedByKey.get(key)
      const amt = parseFloat(String((t as any).amount ?? '0'))
      const existingAmt = existing ? parseFloat(String((existing as any).amount ?? '0')) : -Infinity
      if (!existing || amt > existingAmt) {
        mergedByKey.set(key, t)
      }
    }
  }
  const hyperionTokens: HyperionTokenInfo[] = [...mergedByKey.values()]

  if (!hyperionOk) {
    return priorityBalances
  }

  // 3. Build a set of known contract+symbol combos so we don't duplicate
  const knownKeys = new Set(priorityBalances.map(b => `${b.contract}::${b.symbol}`))

  const getHyperionSymbol = (t: HyperionTokenInfo): string => {
    if (typeof (t as any).currency === 'string') return (t as any).currency
    if (typeof (t as any).symbol === 'string') return (t as any).symbol
    return ''
  }

  const getHyperionDecimals = (t: HyperionTokenInfo): number => {
    const v = (t as any).decimals ?? (t as any).precision
    const n = Number(v)
    return Number.isFinite(n) ? n : 4
  }

  // 4. Map extra Hyperion tokens to TokenBalance (no logoUrl for unknowns)
  const extras: TokenBalance[] = hyperionTokens
    .filter(t => {
      const sym = getHyperionSymbol(t)
      if (!sym) return false
      const key = `${String((t as any).contract)}::${sym}`
      if (knownKeys.has(key)) return false
      const amt = parseFloat(String((t as any).amount ?? '0'))
      return amt > 0
    })
    .map(t => {
      const sym = getHyperionSymbol(t)
      const contract = String((t as any).contract ?? '')
      return {
        symbol: sym,
        contract,
        amount: parseFloat(String((t as any).amount ?? '0')).toFixed(getHyperionDecimals(t)),
        amountRaw: parseFloat(String((t as any).amount ?? '0')),
        name: sym,
        precision: getHyperionDecimals(t),
        logoUrl: resolveTokenLogoUrl(sym, contract),
        usdValue: undefined,
        isKnown: isKnownTokenContract(contract),
      }
    })
    .sort((a, b) => b.amountRaw - a.amountRaw)

  return [...priorityBalances, ...extras]
}
